import React, { useState, useRef, useEffect, useMemo } from "react";
import "./adminDashboard.css";
import {
  MARK_USER_TASK_COMPLETED,
  UPDATE_USER_TASK,
} from "../../../../utils/GraphQL/mutations";
import { GET_USER_INFO_FOR_TASKS } from "../../../../utils/GraphQL/queries";
import { useLazyQuery, useMutation } from "@apollo/client";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import UserTaskCreate from "../../adminComponents/UserTaskModal/UserTaskCreate";
import Auth from "../../../../utils/auth";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { centralDayRange } from "../../../../utils/centralDay";
import useNavHeightVar from "../../../../utils/useNavHeightVar";
import TaskDetailFields from "../../adminComponents/TaskDetailFields/TaskDetailFields";
import AvailSoonerNotePanel from "../../adminComponents/TaskNotePanel/AvailSoonerNotePanel";
import AttentionTiles from "../../adminComponents/AttentionTiles/AttentionTiles";
import { hasRole, hasAnyRole } from '../../../../utils/permissions';

const clearTaskObj = {
  id: "",
  assigneeName: "",
  assigneeId: "",
  taskType: "",
  note: "",
  associatedClientId: "",
  associatedClientName: "",
  active: true,
  dateCreated: "",
  dueDate: "",
};
const AdminDashboardTasks = ({
  tasks,
  queryTasks,
  handleSelectedTask,
  handleTaskModal,
  useTaskModal,
  handleUpdatedTasksArr,
  handleQueryAllTasks,
  queryAllTasks,
  removeTaskAfterReassignment
}) => {
  const { handleSnack } = useSnackbar();
  /* Publishes the nav's MEASURED height as `--bdr-nav-h`, which the console's
     height calc subtracts. The bar is in normal flow and its height is not a
     constant — it changes at MUI's xl breakpoint and with role-dependent menu
     items — so this is measured, not assumed. */
  useNavHeightVar();
  const [markTaskCompleteGQLCall] = useMutation(MARK_USER_TASK_COMPLETED);
  const [updateUserTaskGQLCall] = useMutation(UPDATE_USER_TASK);
  const [getUsersInfo, { data: usersInfo }] = useLazyQuery(
    GET_USER_INFO_FOR_TASKS,
    { fetchPolicy: "network-only" }
  );
  const [afterLastUserIndex, setAfterLastUserIndex] = useState(100);
  const [selectedTaskSortId, setSelectedTaskSortId] = useState("");
  const [perm, setPerm] = useState("");
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [taskObjUpdateBool, setTaskObjUpdateBool] = useState(false);
  const [taskObj, setTaskObj] = useState({ ...clearTaskObj });
  const [pastDue, setPastDue] = useState(false);
  const [shadowOn, setShadowOn] = useState(false);
  const [clientAlreadyAssigned, setClientAlreadyAssigned] = useState(false);
  const tasksBoxRef = useRef();
  const shadowTopRef = useRef();
  const shadowBottomRef = useRef();
  const taskItemsBox = useRef();
  const shadowWrapper = useRef();
  useEffect(() => {
    const { data: user } = Auth.getProfile();
    if (user.permission) {
      setPerm(user.permission);
      if (hasRole(user.permission, 'admin')) {
        getUsersInfo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (usersInfo?.getUsersNamesForTasks?.length > 0) {
      const index = usersInfo?.getUsersNamesForTasks?.length;
      const currentId = Auth?.getProfile()?.data?._id;
      const currentUserIndex = usersInfo?.getUsersNamesForTasks?.map(
        (user, index) => {
          if (user?._id === currentId) {
            return index;
          }
          return false;
        }
      );
      const filteredIndex = currentUserIndex?.filter(
        (item) => item !== false
      )[0];
      setAfterLastUserIndex(index);
      setSelectedUserIndex(filteredIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersInfo]);
  useEffect(() => {
    queryTasks(selectedTaskSortId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskSortId]);
  /*
   * Scroll shadows. Re-measures on a ResizeObserver, not just on `tasks`.
   *
   * WHY AN OBSERVER. A dependency array can only see React-driven changes. The
   * triggers that actually move this box are not all React: the `svh` cap moves
   * with the viewport, fonts and images reflow, and the window resizes. The
   * observer catches those and the `tasks` dep together, so one mechanism
   * covers every case instead of a dep list that is right only sometimes.
   *
   * It watches BOTH refs. The shadows are positioned against `.shadow-wrapper`
   * while the scroll measurement reads `.admin-tasks-btns-box` — two different
   * elements, and the bug this guards against is them disagreeing about height.
   *
   * `.current` is null-guarded: P2 moved where these refs render, and an
   * unguarded `.offsetHeight` on an unmounted node throws.
   */
  useEffect(() => {
    const tasksBoxEl = taskItemsBox.current;
    const wrapperEl = shadowWrapper.current;
    if (!tasksBoxEl) return undefined;

    const handleShadow = () => {
      const contentScroll = tasksBoxEl.scrollHeight - tasksBoxEl.offsetHeight;
      if (contentScroll <= 0) return;
      const currentScroll = tasksBoxEl.scrollTop / contentScroll;
      if (shadowTopRef.current) {
        shadowTopRef.current.style.opacity = currentScroll;
      }
      if (shadowBottomRef.current) {
        shadowBottomRef.current.style.opacity = 1 - currentScroll;
      }
    };

    const measure = () => {
      const overflowing = tasksBoxEl.offsetHeight < tasksBoxEl.scrollHeight;
      setShadowOn(overflowing);
      if (overflowing) handleShadow();
    };

    measure();
    tasksBoxEl.addEventListener("scroll", handleShadow);

    /* Below 900px the right column is not rendered, so nothing beside this box
       can change its height — skip the observer rather than pay for a
       re-measure on every iOS toolbar collapse. */
    let observer;
    if (!useTaskModal && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(tasksBoxEl);
      if (wrapperEl) observer.observe(wrapperEl);
    }

    return () => {
      tasksBoxEl.removeEventListener("scroll", handleShadow);
      if (observer) observer.disconnect();
    };
  }, [tasks, useTaskModal]);
  const unMarkTask = async (taskId) => {
    const { data } = await markTaskCompleteGQLCall({
      variables: {
        taskId,
        active: true,
      },
    });
    if (data?.markCompletedTask) {
      handleSnack("Task Restored", 3000);
      const id = hasRole(perm, 'admin') ? selectedTaskSortId : "";
      queryTasks(id);
      return;
    }
  };
  const markCompleted = async (taskId) => {
    const { data } = await markTaskCompleteGQLCall({
      variables: {
        taskId,
        active: false,
      },
    });
    if (data?.markCompletedTask) {
      handleSnack(
        <div className="admin-task-snack">
          <span className="admin-task-item-snack">Task Completed.</span>
          <button
            className="admin-task-snack-btn"
            onClick={() => {
              unMarkTask(taskId);
            }}
          >
            Restore Task?
          </button>
        </div>,
        8000
      );
      const id = hasRole(perm, 'admin') ? selectedTaskSortId : "";
      queryTasks(id);
      return;
    }
  };
  // The assignee list, built once — both selects listed the same MenuItems.
  // "All" sits past the end of the real users at afterLastUserIndex.
  const assigneeOptions = [
    ...(usersInfo?.getUsersNamesForTasks || []).map((userInfo, index) => ({
      value: index,
      label: `${userInfo?.firstName} ${userInfo?.lastName}`.trim(),
    })),
    { value: afterLastUserIndex, label: "All" },
  ];

  /* Non-empty only when an admin has moved the sort off its default, so the
     Overdue tile can say whose overdue count it is actually showing. */
  /* Once per render, not per row — and Central, to match the schedule's day
     semantics and the Today panel beside it. */
  const todayBounds = useMemo(() => centralDayRange(), []);

  const sortLabel =
    hasRole(perm, "admin") && selectedTaskSortId
      ? assigneeOptions.find((o) => o.value === selectedUserIndex)?.label || ""
      : "";

  const handleChangeAssigneeSort = (index) => {
    const id =
      afterLastUserIndex === index
        ? "all"
        : usersInfo?.getUsersNamesForTasks[index]?._id;
    setSelectedTaskSortId(id);
    setSelectedUserIndex(index);
  };
  const searchResultSet = (result) => {
    setTaskObjUpdateBool(true);
    setTaskObj((prev) => ({
      ...prev,
      associatedClientId: result?._id,
      associatedClientName: `${result?.firstName} ${result?.lastName}`,
    }));
  };
  const handleObjChangeForUpdate = async (eventTarget) => {
    setTaskObjUpdateBool(true);
    //event = string when calendar mills updates
    if (typeof eventTarget === "string") {
      setTaskObj((prev) => ({
        ...prev,
        dueDate: eventTarget,
      }));
      return;
    }
    const { name, value } = eventTarget;
    setTaskObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  /* The assignee arrives already resolved to {assigneeId, assigneeName} —
     TaskDetailFields owns the index↔sentinel mapping. */
  const handleAssigneeChange = ({ assigneeId, assigneeName }) => {
    setTaskObjUpdateBool(true);
    setTaskObj((prev) => ({ ...prev, assigneeId, assigneeName }));
  };
  const handleTaskUpdate = async () => {
    const { data } = await updateUserTaskGQLCall({
      variables: {
        ...taskObj,
        prevAssignedClient: clientAlreadyAssigned,
      },
    });
    if (data?.updateUserTask) {
      setNonModalTask({ ...data?.updateUserTask });
      handleUpdatedTasksArr({ ...data?.updateUserTask });
      handleSnack("Task Updated", 2000);
      return;
    }
    handleSnack("Something went wrong...", 2000);
  };
  /*
   * Deselect. Without this NOTHING ever cleared `taskObj`, so the first click
   * of a session replaced the count tiles with the detail pane permanently —
   * taking the only entry point to Quote Requests with it until a reload.
   */
  const clearNonModalTask = () => {
    setTaskObj({ ...clearTaskObj });
    setTaskObjUpdateBool(false);
    setPastDue(false);
    setClientAlreadyAssigned(false);
  };
  const setNonModalTask = (task) => {
    setTaskObj({ ...task });
    setTaskObjUpdateBool(false);
    setClientAlreadyAssigned(task?.associatedClientId?.length > 0);
    /* No assignee-index derivation here any more — TaskDetailFields computes it
       from (assigneeId, users) with useMemo, so there is nothing to keep in
       sync and nothing to reset. */
    const pastDue = parseInt(task?.dueDate) < Date.now();
    setPastDue(pastDue);
  };
  return (
    <div ref={tasksBoxRef} className="admin-tasks-box">
      {/*
        * Phone: one column, ordered counts -> Today -> My Tasks, per the
        * approved mobile design. Placed here in the DOM rather than reordered
        * with CSS `order`, so the tab sequence matches what is on screen —
        * `order` moves the paint, never the focus.
        */}
      {useTaskModal && (
        <>
          <div className="bdr-section-panel">
            <div className="bdr-section-panel-body">
              <AttentionTiles tasks={tasks} sortLabel={sortLabel} />
            </div>
          </div>
          <AvailSoonerNotePanel
              canSeeAvailSooner={hasAnyRole(perm, ["admin", "office"])}
            />
        </>
      )}
      <div className="admin-tasks-btns-and-display">
        {/* LEFT CARD — header, list and the all-tasks toggle are one panel. */}
        <section className="bdr-section-panel tsk-list-card">
      <div className="bdr-section-panel-hdr">
        <h3 className="bdr-section-panel-title">My Tasks</h3>
        {/* Filter and create share the header line. The filter used to own a
            full-width row of its own below it, which is why it rendered at
            half the page width for a six-character value. */}
        <div className="bdr-section-panel-actions tsk-hdr-controls">
          {usersInfo?.getUsersNamesForTasks?.length > 0 &&
            hasRole(perm, "admin") && (
              <BdrInput
                className="tsk-sort-select"
                name="user-task-select"
                /* The visible label was dropped when this moved onto the
                   header line, so the control needs its own accessible name.
                   BdrInput has no ariaLabel prop — it spreads ...rest onto the
                   control, so the DOM attribute is what lands. */
                aria-label="Sort tasks by assignee"
                options={assigneeOptions}
                value={selectedUserIndex}
                /* parseInt is load-bearing: the dropdown hands back a STRING
                   (both a native <select> and BdrSelectPopup do), and the
                   handlers compare with === against the numeric
                   afterLastUserIndex and index into the user array. */
                onChange={(event) => {
                  handleChangeAssigneeSort(parseInt(event.target.value, 10));
                }}
              />
            )}
          <UserTaskCreate queryTasks={queryTasks} compactLabel />
        </div>
      </div>
        <div className="bdr-section-panel-body">
        <div ref={shadowWrapper} className="shadow-wrapper">
          {shadowOn && (
            <>
              {/* Gradient only, no glyphs. The arrow characters rendered as literal
                  text in the middle of the list; the messages screens
                  dropped theirs for the same reason. */}
              <div ref={shadowTopRef} className="shadow shadow--top"></div>
              <div
                ref={shadowBottomRef}
                className="shadow shadow--bottom"
              ></div>
            </>
          )}
          <div ref={taskItemsBox} className="admin-tasks-btns-box">
            {tasks?.length > 0 ? (
              tasks?.map((task, index) => {
                const typeNameText = task?.associatedClientName?.length
                  ? `${task?.taskType} - ${task?.associatedClientName}`
                  : task?.taskType;
                const dueMs = parseInt(task?.dueDate, 10);
                /* Three states, and they are NOT the same test. "Overdue" is
                   before today STARTED — comparing against Date.now() would
                   call a task due at 5pm overdue all morning. */
                const overdue =
                  Number.isFinite(dueMs) && dueMs < todayBounds.dayStartMs;
                const dueToday =
                  Number.isFinite(dueMs) &&
                  !overdue &&
                  dueMs <= todayBounds.dayEndMs;
                const selected =
                  useTaskModal === false && taskObj?.id === task?.id;
                return (
                  <div
                    key={`${task?.id}-${index}`}
                    className={`tsk-row${selected ? " is-selected" : ""}`}
                  >
                    <button
                      onClick={() => {
                        markCompleted(task?.id);
                      }}
                      className="tsk-row-check"
                      title="Mark Completed"
                    >
                      {/*
                        * Neutral, not green-or-red. A coloured checkbox on
                        * every row is IDENTITY colour: when every normal row
                        * is green, green stops meaning anything and the row
                        * that needs attention cannot win. Status lives in the
                        * Overdue / Due today badge, which is the one place
                        * colour is allowed to speak here.
                        */}
                      <CheckBoxOutlineBlankIcon
                        sx={{ color: "var(--bdr-text)", opacity: 0.75 }}
                        titleAccess="Mark Completed"
                      />
                    </button>
                    <button
                      onClick={() => {
                        if (useTaskModal) {
                          handleSelectedTask(task?.id);
                          handleTaskModal(true);
                          return;
                        }
                        /* Clicking the selected row again returns to the
                           overview — the same affordance as the explicit
                           Back button on the pane. */
                        if (taskObj?.id === task?.id) {
                          clearNonModalTask();
                          return;
                        }
                        setNonModalTask(task);
                      }}
                      className="tsk-row-main"
                      aria-pressed={selected}
                    >
                      <span className="tsk-row-title">{typeNameText}</span>
                      <span className="tsk-row-note">{task?.note}</span>
                    </button>
                    <span className="tsk-row-meta">
                      <span className="tsk-row-due">
                        {BAGroupTimeFormat(task?.dueDate)}
                      </span>
                      {overdue && (
                        <span className="bdr-badge bdr-badge--danger">
                          Overdue
                        </span>
                      )}
                      {dueToday && (
                        <span className="bdr-badge bdr-badge--warning">
                          Due today
                        </span>
                      )}
                    </span>
                  </div>
                );
              })
            ) : tasks === null || tasks === undefined ? (
              /* Not fetched YET. Saying "No Tasks To Display" here is the same
                 lie the count tiles are forbidden from telling — on a slow
                 connection the landing page would assert the board is empty
                 before we have looked. */
              <div className="admin-tasks-skeletons" aria-hidden>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="admin-task-skeleton" />
                ))}
              </div>
            ) : (
              <h3>No Tasks To Display</h3>
            )}
          </div>
        </div>
          {hasRole(perm, "admin") && (
            <div className="tsk-footer">
              {/* `action`: it changes what the list is showing — a view
                  switch, not a commit and not a cancel. */}
              <BdrButton variant="action" size="sm" onClick={handleQueryAllTasks}>
                {queryAllTasks ? "← Upcoming only" : "Show all tasks"}
              </BdrButton>
            </div>
          )}
        </div>
        </section>
        {/*
          * THE RIGHT COLUMN — desktop only. Three cards: the day, the counts,
          * and the selected task. Each is its own panel rather than regions of
          * one box, so the boundaries are visible and a scrollbar inside one
          * cannot crowd the heading of the next.
          */}
        {useTaskModal === false && (
          <div className="admin-tasks-right-col">
            <AvailSoonerNotePanel
              canSeeAvailSooner={hasAnyRole(perm, ["admin", "office"])}
            />
            <div className="bdr-section-panel tsk-tiles-card">
              <div className="bdr-section-panel-body">
                <AttentionTiles tasks={tasks} sortLabel={sortLabel} />
              </div>
            </div>
            <section className="bdr-section-panel tsk-detail-card">
              <div className="bdr-section-panel-hdr">
                <h3 className="bdr-section-panel-title">Task</h3>
                {taskObj?.id && (
                  <div className="bdr-section-panel-actions">
                    {/* `action`: Back changes the view. It sits LEFT of the
                        status badge so the badge stays the rightmost thing in
                        the bar, the way the modal shell orders them. */}
                    <BdrButton
                      variant="action"
                      size="sm"
                      onClick={clearNonModalTask}
                    >
                      ← Back
                    </BdrButton>
                    {pastDue && (
                      <span className="bdr-badge bdr-badge--danger">
                        Past Due
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="bdr-section-panel-body bdr-section-panel-body--scroll">
              {taskObj?.id ? (
              <div className="admin-tasks-non-modal-display">
                <TaskDetailFields
                  task={taskObj}
                  users={usersInfo?.getUsersNamesForTasks}
                  onFieldChange={handleObjChangeForUpdate}
                  onAssigneeChange={handleAssigneeChange}
                  onClientPicked={searchResultSet}
                />
                <div className="tsk-detail-actions">
                  {taskObjUpdateBool && (
                    <BdrButton variant="confirm" size="sm" onClick={handleTaskUpdate}>
                      Update
                    </BdrButton>
                  )}
                  <BdrButton
                    variant={taskObj?.active ? "confirm" : "default"}
                    size="sm"
                    onClick={() => {
                      if (taskObj?.active) {
                        markCompleted(taskObj?.id);
                        return;
                      }
                      unMarkTask(taskObj?.id);
                    }}
                  >
                    {taskObj?.active ? "Mark Completed" : "Mark Incomplete"}
                  </BdrButton>
                </div>
              </div>
              ) : (
                <p className="tsk-detail-empty">
                  Select a task to see its details.
                </p>
              )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};
export default AdminDashboardTasks;
